import { useState } from 'react';
import { Building2, Loader2, Save, User } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { cn } from '../../lib/cn';
import { validaCliente, type CampoCliente, type ClienteInserito } from '@eventi/datiCliente.ts';

type Props = {
  iniziale: ClienteInserito;
  /** Gli errori che ha trovato il server sui dati di partenza. */
  erroriIniziali: Partial<Record<CampoCliente, string>> | null;
  /** Da dove vengono i dati: cambia solo la spiegazione. */
  fonte: 'ordine' | 'scheda' | null;
  inCorso: boolean;
  onSalva: (cliente: ClienteInserito) => void;
  onAnnulla?: () => void;
};

const SENZA_CONTATTI = { email: null, telefono: null, referente: null };

type CampoTesto = Exclude<CampoCliente, 'tipo'>;

/** Come si dispone ogni campo: etichetta, larghezza nella griglia, suggerimento. */
const CAMPI: { campo: CampoTesto; etichetta: (t: ClienteInserito['tipo']) => string; largo?: string; suggerimento?: string; solo?: ClienteInserito['tipo'] }[] = [
  { campo: 'denominazione', etichetta: (t) => (t === 'azienda' ? 'Ragione sociale' : 'Nome e cognome'), largo: 'sm:col-span-6' },
  { campo: 'partitaIva', etichetta: () => 'Partita IVA', largo: 'sm:col-span-3', suggerimento: '11 cifre', solo: 'azienda' },
  { campo: 'codiceFiscale', etichetta: (t) => (t === 'azienda' ? 'Codice fiscale (se diverso)' : 'Codice fiscale'), largo: 'sm:col-span-3', suggerimento: '16 caratteri' },
  { campo: 'codiceSdi', etichetta: () => 'Codice SDI', largo: 'sm:col-span-3', suggerimento: 'vuoto se non c\'è' },
  { campo: 'pec', etichetta: () => 'PEC', largo: 'sm:col-span-3', suggerimento: 'facoltativa' },
  { campo: 'indirizzo', etichetta: () => 'Indirizzo', largo: 'sm:col-span-6', suggerimento: 'via e numero' },
  { campo: 'cap', etichetta: () => 'CAP', largo: 'sm:col-span-2' },
  { campo: 'citta', etichetta: () => 'Città', largo: 'sm:col-span-3' },
  { campo: 'provincia', etichetta: () => 'Prov.', largo: 'sm:col-span-1', suggerimento: 'PE' },
];

/**
 * I dati del cliente della fattura, da completare o correggere a mano: quello
 * che il checkout del sito non ha raccolto. Il controllo e' lo stesso del
 * server (datiCliente.ts): gli errori si vedono mentre si scrive, sui campi
 * gia' toccati o gia' segnalati.
 */
export function ModuloClienteFattura({ iniziale, erroriIniziali, fonte, inCorso, onSalva, onAnnulla }: Props) {
  const [valori, setValori] = useState<ClienteInserito>(iniziale);
  const [toccati, setToccati] = useState<Set<CampoCliente>>(() => new Set(Object.keys(erroriIniziali ?? {}) as CampoCliente[]));

  const esito = validaCliente(valori, SENZA_CONTATTI);
  const errori = esito.ok ? {} : esito.errori;

  const cambia = (campo: CampoCliente, valore: string) => {
    setValori((v) => ({ ...v, [campo]: valore }));
    setToccati((t) => new Set(t).add(campo));
  };

  const salva = () => {
    if (!esito.ok) {
      setToccati(new Set(Object.keys(errori) as CampoCliente[]));
      return;
    }
    onSalva(valori);
  };

  return (
    <section className="space-y-4">
      <div>
        <h4 className="text-sm font-semibold text-gray-900">Dati del cliente</h4>
        <p className="mt-0.5 text-xs text-neutral-500">
          {fonte === 'scheda' ? 'Dalla scheda in Clienti collegata a questo ordine.' : 'Da quello che ha scritto chi ha comprato sul sito.'} Si
          salvano nella scheda del cliente in Clienti; se il cliente è già su Fatture in Cloud si completano solo i campi vuoti.
        </p>
      </div>

      <div role="radiogroup" aria-label="Tipo di cliente" className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
        {([
          { tipo: 'privato', testo: 'Privato', Icona: User },
          { tipo: 'azienda', testo: 'Azienda', Icona: Building2 },
        ] as const).map(({ tipo, testo, Icona }) => (
          <button
            key={tipo}
            type="button"
            role="radio"
            aria-checked={valori.tipo === tipo}
            onClick={() => cambia('tipo', tipo)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              valori.tipo === tipo ? 'bg-white text-gray-900 shadow-sm' : 'text-neutral-500 hover:text-gray-800',
            )}
          >
            <Icona className="h-4 w-4" /> {testo}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
        {CAMPI.filter((c) => !c.solo || c.solo === valori.tipo).map(({ campo, etichetta, largo, suggerimento }) => {
          const errore = toccati.has(campo) ? errori[campo] : undefined;
          const id = `cliente-${campo}`;
          return (
            <div key={campo} className={cn('space-y-1', largo)}>
              <label htmlFor={id} className="text-xs font-medium text-gray-700">
                {etichetta(valori.tipo)}
              </label>
              <Input
                id={id}
                value={valori[campo]}
                placeholder={suggerimento}
                onChange={(e) => cambia(campo, e.target.value)}
                aria-invalid={Boolean(errore)}
                aria-describedby={errore ? `${id}-errore` : undefined}
                className={cn('h-9', errore && 'border-red-400 focus-visible:ring-red-300')}
              />
              {errore && (
                <p id={`${id}-errore`} className="text-[11px] leading-tight text-red-600">
                  {errore}
                </p>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        {onAnnulla && (
          <Button variant="outline" size="sm" onClick={onAnnulla} disabled={inCorso}>
            Annulla
          </Button>
        )}
        <Button size="sm" onClick={salva} disabled={inCorso}>
          {inCorso ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          Salva in Clienti
        </Button>
      </div>
    </section>
  );
}
